import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, Button, Input, Badge, Spinner } from 'reactstrap';
import moment from 'moment';
import { Calendar, Coffee, Sun, Moon, Printer, RefreshCw } from 'react-feather';
import { Get_Meal_Pass_Report } from '../../api/MealPassApi/MealPassApi';

const MealPassReport = ({ refreshSignal, selectedTrustId }) => {
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);

  const fetchReport = async (date) => {
    setLoading(true);
    try {
      const params = { date };
      if (selectedTrustId) {
        params.trustId = selectedTrustId;
      }
      const { data } = await Get_Meal_Pass_Report(params);
      if (data?.status === 200 && Array.isArray(data.payload)) {
        setReportData(data.payload);
      } else {
        setReportData([]);
      }
    } catch (err) {
      console.error('Error fetching meal pass report:', err);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(selectedDate);
  }, [selectedDate, refreshSignal, selectedTrustId]);

  const handleQuickDate = (offsetDays) => {
    const newDate = moment().add(offsetDays, 'days').format('YYYY-MM-DD');
    setSelectedDate(newDate);
  };

  // Calculations
  const totalPasses = reportData.reduce((acc, curr) => acc + Number(curr.total_pass || 0), 0);
  const totalExpected = reportData.reduce((acc, curr) => acc + Number(curr.total_number_of_person || 0), 0);
  const totalServed = reportData.reduce((acc, curr) => acc + Number(curr.total_number_of_taken_person || 0), 0);
  const totalRemaining = Math.max(0, totalExpected - totalServed);
  const overallPercentage = totalExpected > 0 ? Math.round((totalServed / totalExpected) * 100) : 0;

  const getSlotIcon = (type) => {
    switch (type) {
      case 'breakFast':
        return <Coffee size={20} className="text-warning" />;
      case 'lunch':
        return <Sun size={20} className="text-danger" />;
      case 'dinner':
        return <Moon size={20} className="text-info" />;
      default:
        return <Coffee size={20} className="text-primary" />;
    }
  };

  const getSlotLabel = (type, index) => {
    if (type === 'breakFast' || index === 0) return 'Breakfast (अल्पाहार)';
    if (type === 'lunch' || index === 1) return 'Lunch (दोपहर भोजन / महाप्रसाद)';
    if (type === 'dinner' || index === 2) return 'Dinner (रात्रि भोजन / महाप्रसाद)';
    return type || 'Meal Slot';
  };

  const isToday = selectedDate === moment().format('YYYY-MM-DD');
  const currTime = moment().utcOffset('+05:30').format('HH:mm:ss');

  const getSlotStatus = (slot) => {
    if (!isToday) {
      return moment(selectedDate).isBefore(moment().format('YYYY-MM-DD'))
        ? { label: 'Completed', color: 'secondary' }
        : { label: 'Upcoming', color: 'info' };
    }
    if (slot.startTime && slot.endTime) {
      if (currTime >= slot.startTime && currTime <= slot.endTime) {
        return { label: 'Active Now (जारी है)', color: 'success' };
      }
      if (currTime > slot.endTime) {
        return { label: 'Closed (समाप्त)', color: 'secondary' };
      }
      return { label: 'Upcoming (आने वाला)', color: 'primary' };
    }
    return { label: 'Scheduled', color: 'primary' };
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="meal-report-wrapper">
      {/* Date Filter & Control Bar */}
      <Card className="meal-card-kpi mb-4">
        <CardBody className="p-3">
          <Row className="align-items-center g-3">
            <Col lg="4" md="5" sm="12" className="d-flex align-items-center gap-2 flex-wrap">
              <Calendar className="text-primary flex-shrink-0" size={18} />
              <span className="fw-bold text-dark text-nowrap">Select Date:</span>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="form-control"
                style={{ minWidth: '150px', flex: '1 1 150px' }}
              />
            </Col>
            <Col lg="4" md="4" sm="12" className="d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                color={selectedDate === moment().format('YYYY-MM-DD') ? 'primary' : 'outline-primary'}
                onClick={() => handleQuickDate(0)}
                className="flex-fill"
              >
                Today
              </Button>
              <Button
                size="sm"
                color={selectedDate === moment().add(1, 'days').format('YYYY-MM-DD') ? 'primary' : 'outline-primary'}
                onClick={() => handleQuickDate(1)}
                className="flex-fill"
              >
                Tomorrow
              </Button>
              <Button
                size="sm"
                color={selectedDate === moment().subtract(1, 'days').format('YYYY-MM-DD') ? 'primary' : 'outline-primary'}
                onClick={() => handleQuickDate(-1)}
                className="flex-fill"
              >
                Yesterday
              </Button>
            </Col>
            <Col lg="4" md="3" sm="12" className="text-md-end">
              <div className="d-flex gap-2 justify-content-start justify-content-md-end flex-wrap">
                <Button size="sm" color="outline-secondary" onClick={() => fetchReport(selectedDate)} disabled={loading} className="flex-fill flex-md-grow-0">
                  <RefreshCw size={14} className={loading ? 'fa-spin' : ''} /> Refresh
                </Button>
                <Button size="sm" color="outline-dark" onClick={handlePrint} className="flex-fill flex-md-grow-0">
                  <Printer size={14} className="me-1" /> Print Report
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* KPI Cards */}
      <Row className="g-3 mb-4">
        <Col lg="3" sm="6">
          <Card className="meal-card-kpi h-100">
            <CardBody className="p-3 d-flex align-items-center gap-3">
              <div className="meal-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                🎟️
              </div>
              <div>
                <div className="text-muted small fw-semibold">TOTAL ACTIVE PASSES</div>
                <h3 className="fw-bold mb-0 text-dark">{totalPasses}</h3>
                <small className="text-muted">Issued for {moment(selectedDate).format('DD MMM')}</small>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg="3" sm="6">
          <Card className="meal-card-kpi h-100">
            <CardBody className="p-3 d-flex align-items-center gap-3">
              <div className="meal-kpi-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                👥
              </div>
              <div>
                <div className="text-muted small fw-semibold">EXPECTED DINERS</div>
                <h3 className="fw-bold mb-0 text-dark">{totalExpected}</h3>
                <small className="text-muted">Headcount forecasted</small>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg="3" sm="6">
          <Card className="meal-card-kpi h-100">
            <CardBody className="p-3 d-flex align-items-center gap-3">
              <div className="meal-kpi-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
                🍽️
              </div>
              <div>
                <div className="text-muted small fw-semibold">MEALS SERVED</div>
                <h3 className="fw-bold mb-0 text-success">{totalServed}</h3>
                <small className="text-success fw-semibold">{overallPercentage}% completion</small>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg="3" sm="6">
          <Card className="meal-card-kpi h-100">
            <CardBody className="p-3 d-flex align-items-center gap-3">
              <div className="meal-kpi-icon" style={{ background: '#f8fafc', color: '#64748b' }}>
                ⏳
              </div>
              <div>
                <div className="text-muted small fw-semibold">PENDING TO BE SERVED</div>
                <h3 className="fw-bold mb-0 text-secondary">{totalRemaining}</h3>
                <small className="text-muted">Remaining persons</small>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Time Slot Cards Grid */}
      <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
        <span>Bhojanshala Time Slots Breakdown ({moment(selectedDate).format('DD MMMM YYYY')})</span>
      </h5>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading kitchen report...</p>
        </div>
      ) : reportData.length === 0 ? (
        <Card className="p-4 text-center text-muted">
          <p className="mb-0">No meal passes found for {moment(selectedDate).format('DD MMMM YYYY')}.</p>
        </Card>
      ) : (
        <Row className="g-3 mb-4">
          {reportData.map((slot, idx) => {
            const expected = Number(slot.total_number_of_person || 0);
            const served = Number(slot.total_number_of_taken_person || 0);
            const remaining = Math.max(0, expected - served);
            const percentage = expected > 0 ? Math.round((served / expected) * 100) : 0;
            const status = getSlotStatus(slot);

            return (
              <Col lg="4" md="6" key={slot.mealTimeSlotId || idx}>
                <div className="meal-slot-card h-100">
                  <div className="meal-slot-header">
                    <div className="d-flex align-items-center gap-2">
                      {getSlotIcon(slot.slotType)}
                      <div>
                        <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>
                          {getSlotLabel(slot.slotType, idx)}
                        </div>
                        {slot.startTime && slot.endTime && (
                          <div className="text-muted small">
                            {moment(slot.startTime, 'HH:mm:ss').format('hh:mm A')} -{' '}
                            {moment(slot.endTime, 'HH:mm:ss').format('hh:mm A')}
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge color={status.color} className="px-2 py-1">
                      {status.label}
                    </Badge>
                  </div>

                  <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small text-muted">Consumption Progress</span>
                      <span className="fw-bold small">{percentage}%</span>
                    </div>

                    <div className="slot-progress-bar">
                      <div
                        className="slot-progress-fill"
                        style={{
                          width: `${Math.min(100, percentage)}%`,
                          background: percentage >= 80 ? '#10b981' : percentage > 30 ? '#f59e0b' : '#3b82f6'
                        }}
                      />
                    </div>

                    <Row className="text-center pt-2 g-1">
                      <Col xs="4" className="border-end meal-slot-stats-col">
                        <div className="text-muted stat-label">
                          EXPECTED
                        </div>
                        <div className="fw-bold fs-6 text-dark">{expected}</div>
                      </Col>
                      <Col xs="4" className="border-end meal-slot-stats-col">
                        <div className="text-muted stat-label">
                          SERVED
                        </div>
                        <div className="fw-bold fs-6 text-success">{served}</div>
                      </Col>
                      <Col xs="4" className="meal-slot-stats-col">
                        <div className="text-muted stat-label">
                          PENDING
                        </div>
                        <div className="fw-bold fs-6 text-warning">{remaining}</div>
                      </Col>
                    </Row>

                    <div className="mt-3 pt-2 border-top text-center text-muted small">
                      Total passes booked: <strong>{slot.total_pass || 0}</strong>
                    </div>
                  </CardBody>
                </div>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default MealPassReport;
